import { useNavigate } from 'react-router-dom';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, Import, Info } from 'lucide-react';
import { Banner, Button, Drawer, Field, Input } from '@/components/ui';
import {
  BackupPolicySelect,
  NodeSelect,
  TemplateVersionSelect,
  type SitePolicy,
} from '@/components/sites/SiteFormFields';
import { idempotencyKey } from '@/utils/idempotency';
import { useStartImport } from '@/hooks/useImports';
import { useNodes } from '@/hooks/useNodes';
import { useActiveTemplates } from '@/hooks/useTemplates';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { isSchedulable } from '@/utils/nodes';
import { suggestSlug } from '@/utils/slug';
import { isDomainName, isSlug, splitList } from '@/utils/validation';
import type { SiteDetail } from '@/types/api';

/**
 * Importing an existing WordPress (P7): the new site's own settings. Its
 * content comes later, from the old server; its names keep pointing there
 * until the operator switches them. From a watched site, its name and domains
 * are filled in, and watching it ends when the import starts — the imported
 * site is checked from then on.
 */
export function ImportSiteDrawer({
  open,
  onClose,
  from,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly from?: SiteDetail;
}): ReactNode {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(from?.displayName ?? '');
  const [slug, setSlug] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState(from?.primaryDomain ?? '');
  const [aliases, setAliases] = useState(from ? from.domains.filter((domain) => domain.role !== 'primary').map((domain) => domain.fqdn).join(' ') : '');
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [backupPolicy, setBackupPolicy] = useState<SitePolicy>('standard');
  const key = useRef(idempotencyKey('site.import'));

  const templates = useActiveTemplates({ enabled: open });
  const nodes = useNodes({ enabled: open });
  const versions = templates.data ?? [];
  const chosenVersion = templateVersionId || versions[0]?.id || '';
  const schedulable = (nodes.data ?? []).filter(isSchedulable);
  const effectiveSlug = slug || suggestSlug(displayName);
  const aliasList = splitList(aliases);
  const valid = displayName.trim() && isSlug(effectiveSlug) && isDomainName(primaryDomain.trim().toLowerCase())
    && aliasList.every(isDomainName) && chosenVersion;

  const start = useStartImport({
    onSuccess: (result) => {
      toast.success('Импорт начат', 'Команда для старого сервера — на странице сайта');
      key.current = idempotencyKey('site.import');
      onClose();
      navigate(`/sites/${result.siteId}`);
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!valid || start.isPending) return;
    start.mutate({
      input: {
        slug: effectiveSlug,
        displayName: displayName.trim(),
        primaryDomain: primaryDomain.trim().toLowerCase(),
        aliases: aliasList,
        templateVersionId: chosenVersion,
        ...(nodeId ? { nodeId } : {}),
        tier: 'production',
        backupPolicy,
      },
      key: key.current,
      from,
    });
  };

  return (
    <Drawer
      open={open}
      title="Импорт WordPress"
      description="Перенос сайта с другого сервера; старый сервер работает до переключения DNS"
      onClose={onClose}
      width="md"
      footer={
        <>
          <Button onClick={onClose} disabled={start.isPending}>
            Отмена
          </Button>
          <Button type="submit" form="import-site" variant="primary" icon={Import} busy={start.isPending} disabled={!valid}>
            Начать импорт
          </Button>
        </>
      }
    >
      <form id="import-site" className="stack" onSubmit={submit} noValidate>
        <Field label="Название" htmlFor="import-name">
          <Input id="import-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={160} autoComplete="off" autoFocus />
        </Field>
        <Field label="Идентификатор" htmlFor="import-slug" hint="Имя базы данных и контейнера на новом сервере">
          <Input id="import-slug" className="mono" value={effectiveSlug} onChange={(event) => setSlug(event.target.value.toLowerCase())} maxLength={63} autoComplete="off" spellCheck={false} />
        </Field>
        <Field label="Основной домен" htmlFor="import-domain" hint="Тот, на котором сайт работает сейчас">
          <Input id="import-domain" className="mono" value={primaryDomain} onChange={(event) => setPrimaryDomain(event.target.value)} placeholder="example.com" autoComplete="off" spellCheck={false} />
        </Field>
        <Field label="Дополнительные домены" htmlFor="import-aliases" hint="Через пробел или запятую, например www-версия">
          <Input id="import-aliases" className="mono" value={aliases} onChange={(event) => setAliases(event.target.value)} placeholder="www.example.com" autoComplete="off" spellCheck={false} />
        </Field>
        <div className="form-grid">
          <Field label="Шаблон" htmlFor="import-template" hint="Рекомендуется та же версия PHP, что на старом сервере">
            <TemplateVersionSelect
              id="import-template"
              value={chosenVersion}
              onChange={setTemplateVersionId}
              versions={versions}
              loading={templates.isPending}
            />
          </Field>
          <Field label="Сервер" htmlFor="import-node">
            <NodeSelect id="import-node" value={nodeId} onChange={setNodeId} nodes={schedulable} />
          </Field>
          <Field label="Политика копий" htmlFor="import-policy">
            <BackupPolicySelect id="import-policy" value={backupPolicy} onChange={setBackupPolicy} />
          </Field>
        </div>
        <Banner tone="info" icon={Info}>
          Содержимое и учётные записи переносятся со старого сервера. DNS переключается вручную после проверки.
          {from ? ' Мониторинг перейдёт на импортированный сайт.' : ''}
        </Banner>
        {start.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(start.error, 'Не удалось начать импорт')}
          </p>
        ) : null}
      </form>
    </Drawer>
  );
}
